/**
 * Iter148 — Öffentliche Anleitung zur Datenlöschung (Meta-Requirement).
 *
 * Meta verlangt eine eigene Seite mit klaren Schritten zur Datenlöschung.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Mail, MessageCircle, Trash2 } from "lucide-react";

export default function DataDeletion() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Bitte gültige E-Mail-Adresse angeben");
      return;
    }
    setBusy(true);
    try {
      await apiClient.post("/public/data-deletion-request", { email, reason });
      toast.success("Anfrage eingegangen. Wir melden uns innerhalb von 30 Tagen.");
      setEmail("");
      setReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold text-slate-900">Buchhaltung CH</Link>
          <nav className="text-sm space-x-4 text-slate-600">
            <Link to="/privacy" className="hover:text-slate-900">Datenschutz</Link>
            <Link to="/terms" className="hover:text-slate-900">AGB</Link>
            <Link to="/login" className="text-indigo-600 hover:text-indigo-700">Anmelden</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Datenlöschung</h1>
        <p className="text-slate-500 mb-8 text-sm">Anleitung gemäss Art. 15 DSG / Art. 17 DSGVO</p>

        <section className="prose prose-slate max-w-none">
          <p className="text-slate-700 leading-relaxed mb-6">
            Du kannst jederzeit die Löschung deiner personenbezogenen Daten verlangen.
            Beachte: <strong>Buchhaltungsbelege unterliegen einer 10-jährigen Aufbewahrungspflicht</strong> (Art. 958f OR).
            Diese Daten werden erst nach Ablauf der Frist gelöscht. Alle anderen Daten
            (WhatsApp-Nummer, Nutzungslogs, Session-Daten) werden sofort gelöscht.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mb-10">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm">1</div>
                <h3 className="font-semibold text-slate-900 text-sm m-0">Anfrage senden</h3>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed m-0">
                Formular unten ausfüllen oder E-Mail an <code>datenschutz@ch-buchhaltung.ch</code>.
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm">2</div>
                <h3 className="font-semibold text-slate-900 text-sm m-0">Identität prüfen</h3>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed m-0">
                Wir schicken einen Bestätigungslink an deine registrierte E-Mail (Anti-Missbrauch).
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm">3</div>
                <h3 className="font-semibold text-slate-900 text-sm m-0">Bestätigung</h3>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed m-0">
                Innerhalb von 30 Tagen erhältst du eine Löschungsbestätigung mit Konfirmations-Code.
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-8">
            <h3 className="text-sm font-semibold text-amber-900 mb-1">Sofort-Löschung (in-App)</h3>
            <p className="text-xs text-amber-800 leading-relaxed m-0">
              Diese Bereiche kannst du direkt in der App löschen (kein Ticket nötig):
            </p>
            <ul className="text-xs text-amber-800 mt-2 space-y-0.5 list-disc pl-4">
              <li><strong>WhatsApp-Verknüpfung:</strong> Einstellungen → WhatsApp → &bdquo;Nummer entkoppeln&ldquo;</li>
              <li><strong>Einzelne Belege:</strong> Belege → Beleg wählen → Papierkorb-Symbol (nur wenn nicht verbucht)</li>
              <li><strong>Kontakte:</strong> Kontakte → Kontakt bearbeiten → &bdquo;Löschen&ldquo;</li>
            </ul>
          </div>

          <h2 className="text-lg font-semibold text-slate-900 mb-3">Löschung per Formular anfordern</h2>
          <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-white p-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="dd-email">
                Deine registrierte E-Mail-Adresse
              </label>
              <Input
                id="dd-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="max@example.ch"
                required
                data-testid="deletion-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="dd-reason">
                Optional: Grund (hilft uns, den Service zu verbessern)
              </label>
              <Input
                id="dd-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="z.B. nutze eine andere Software"
                data-testid="deletion-reason-input"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="deletion-submit-btn"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Löschung anfordern
            </Button>
          </form>

          <div className="mt-8 grid md:grid-cols-2 gap-3">
            <a
              href="mailto:datenschutz@ch-buchhaltung.ch"
              className="rounded-lg border border-slate-200 bg-white p-3 hover:bg-slate-50 flex items-center gap-2"
            >
              <Mail className="h-5 w-5 text-slate-600" />
              <div>
                <div className="text-sm font-medium text-slate-900">Per E-Mail</div>
                <div className="text-xs text-slate-500">datenschutz@ch-buchhaltung.ch</div>
              </div>
            </a>
            <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-slate-600" />
              <div>
                <div className="text-sm font-medium text-slate-900">Antwort in</div>
                <div className="text-xs text-slate-500">≤ 30 Tage nach Identitätsprüfung</div>
              </div>
            </div>
          </div>
        </section>

        <footer className="mt-16 pt-6 border-t border-slate-200 text-sm text-slate-500">
          <p>Siehe auch <Link to="/privacy" className="text-indigo-600">Datenschutzerklärung</Link>.</p>
        </footer>
      </main>
    </div>
  );
}
